import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Row,
  Col,
  Select,
  DatePicker,
  Button,
  Space,
  Segmented,
  Table,
  Tag,
  Tooltip as AntTooltip,
  message,
  Empty,
  Spin,
  Modal,
  Input,
} from 'antd';
import {
  SearchOutlined,
  DownloadOutlined,
  TrophyOutlined,
  DollarOutlined,
  WalletOutlined,
  GiftOutlined,
  TeamOutlined,
  GlobalOutlined,
  CheckCircleFilled,
  ClockCircleFilled,
  CloseCircleFilled,
  InfoCircleOutlined,
  RiseOutlined,
} from '@ant-design/icons';
import { useMsal } from '@azure/msal-react';
import dayjs, { Dayjs } from 'dayjs';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  LineChart,
  Line,
} from 'recharts';
import { fetchInsightsDashboard } from '../../Services/dataServices';
import '../Atoms/AwardsInsights.css';
import AwardsDrilldown, { AwardDetail } from './AwardsDrilldown';

/* ─────────────────────────── design tokens ───────────────────────────
   Validated categorical + status steps for a white chart surface. Three
   light-mode series steps sit below 3:1 against white, so every chart here
   ships visible value labels and a table view — never colour alone.        */
const VIZ = {
  series1: '#2a78d6',
  series2: '#eb6834',
  series3: '#1baf7a',
  good: '#0ca30c',
  warning: '#fab219',
  critical: '#d03b3b',
  ink: '#0b0b0b',
  ink2: '#52514e',
  muted: '#898781',
  grid: '#e1e0d9',
  axis: '#c3c2b7',
  surface: '#ffffff',
  brand: '#8629A4',
};

const STATUS_STYLE: Record<string, { color: string; icon: React.ReactNode; hint: string }> = {
  Awarded: { color: VIZ.good, icon: <CheckCircleFilled />, hint: 'Points credited to the recipient' },
  Approved: { color: VIZ.series1, icon: <CheckCircleFilled />, hint: 'Cleared approvals, payout pending' },
  Pending: { color: VIZ.warning, icon: <ClockCircleFilled />, hint: 'Awaiting an approver' },
  Rejected: { color: VIZ.critical, icon: <CloseCircleFilled />, hint: 'Declined at some level' },
};

/* ─────────────────────────── payload types ─────────────────────────── */
interface ManagerOption {
  id: string;
  name: string;
  email: string;
  country: string | null;
  location: string | null;
}

interface AwardTypeGroup {
  parent: string;
  types: Array<{ id: string; name: string }>;
}

interface FilterOptions {
  countries: string[];
  locations: string[];
  awardTypes: string[];
  /** Leaf award types grouped under their parent category, for the grouped dropdown. */
  awardTypeGroups: AwardTypeGroup[];
  departments: string[];
  businessUnits: string[];
  managers: ManagerOption[];
  unitHeads: ManagerOption[];
}

interface DashboardPayload {
  range: { fromDate: string; toDate: string; trendEnd: string; granularity: string };
  /** Present only on the first-paint request, which asks for it via includeFilters. */
  filters: FilterOptions | null;
  summary: {
    totalAwards: number;
    awardedAwards: number;
    pendingAwards: number;
    rejectedAwards: number;
    awardTypesInPlay: number;
    uniqueRecipients: number;
    uniqueManagers: number;
    countriesCovered: number;
    filteredSpend: number;
    averageAwardValue: number;
    approvalRate: number;
    footfallVisits: number;
    footfallUniqueVisitors: number;
  };
  budget: {
    totalBudget: number;
    usedBudget: number;
    balance: number;
    utilizationPct: number;
    cycleComments: string | null;
    lastUpdated: string | null;
  };
  gem: {
    allocated: number;
    used: number;
    remaining: number;
    unitHeads: number;
    unitHeadsExhausted: number;
    utilizationPct: number;
    byUnitHead: Array<{
      unitHeadUserId: string;
      unitHeadName: string;
      country: string;
      location: string;
      allocated: number;
      used: number;
      remaining: number;
    }>;
  };
  awardsByType: Array<{ awardType: string; awards: number; amount: number; recipients: number }>;
  approvalStatus: Array<{ status: string; awards: number; amount: number }>;
  topManagers: Array<{
    managerId: string;
    managerName: string;
    managerEmail: string | null;
    country: string;
    usedBudget: number;
    awards: number;
    recipients: number;
    allocatedBudget: number;
    allocatedUsed: number;
    allocatedBalance: number;
  }>;
  /**
   * Manager budget allocation and balance. `recordedUsed` comes straight from
   * managerbudgets.used_budget, which is 0 on every row in this database because the
   * award flow charges MainBudget on the admin path — hence the explicit flag rather
   * than presenting a misleading $0 as actual spend.
   */
  managerBudget: {
    managers: number;
    allocated: number;
    recordedUsed: number;
    balance: number;
    recordedUsedIsUnpopulated: boolean;
  };
  topCountries: Array<{ country: string; awards: number; amount: number; recipients: number }>;
  topLocations: Array<{ location: string; awards: number; amount: number; recipients: number }>;
  topDepartments: Array<{ department: string; awards: number; amount: number; recipients: number }>;
  /** One row per award in the slice, for the grouped recipients drill-down. */
  awardDetails?: AwardDetail[];
  awardsByBusinessUnit: Array<{ businessUnit: string; awards: number; amount: number; recipients: number }>;
  awardsByCoreValue: Array<{ coreValue: string; awards: number; amount: number }>;
  topRecipients: Array<{
    nomineeName: string | null;
    nomineeEmail: string | null;
    country: string;
    awards: number;
    amount: number;
  }>;
  footfallTrend: Array<{ label: string; periodStart: string; visits: number; uniqueVisitors: number }>;
  awardTrend: Array<{ label: string; periodStart: string; awards: number; amount: number }>;
}

/* ─────────────────────────── formatters ─────────────────────────── */
const num = (v: number | undefined | null) => (v ?? 0).toLocaleString('en-US');

const usd = (v: number | undefined | null) =>
  `$${(v ?? 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const usdExact = (v: number | undefined | null) =>
  `$${(v ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const compactUsd = (v: number | undefined | null) => {
  const n = v ?? 0;
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
};

const compactNum = (v: number) => (Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(1)}K` : `${v}`);

const truncate = (text: string, max: number) => (text.length > max ? `${text.slice(0, max - 1)}…` : text);

/* ─────────────────────────── small building blocks ─────────────────────────── */

const Meter: React.FC<{ pct: number; color: string }> = ({ pct, color }) => (
  <div className="ins-meter">
    <div
      className="ins-meter-fill"
      style={{ width: `${Math.min(100, Math.max(0, pct))}%`, background: color }}
    />
  </div>
);

/** Names the filters a figure can't honour, so a static number never reads as a bug. */
const ScopeNote: React.FC<{ note: string | null }> = ({ note }) =>
  note ? (
    <div className="ins-scope-note">
      <InfoCircleOutlined /> {note}
    </div>
  ) : null;

const StatTile: React.FC<{
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  accent?: string;
  icon?: React.ReactNode;
  hero?: boolean;
  meterPct?: number;
  hint?: string;
  scopeNote?: string | null;
}> = ({ label, value, sub, accent = VIZ.series1, icon, hero, meterPct, hint, scopeNote }) => (
  <div className="ins-tile" style={{ ['--ins-tile-accent' as any]: accent }}>
    <div className="ins-tile-label">
      {icon}
      <span>{label}</span>
      {hint && (
        <AntTooltip title={hint}>
          <InfoCircleOutlined style={{ color: VIZ.muted, fontSize: 11 }} />
        </AntTooltip>
      )}
    </div>
    <div className={`ins-tile-value${hero ? ' is-hero' : ''}`}>{value}</div>
    {typeof meterPct === 'number' && <Meter pct={meterPct} color={accent} />}
    {sub && <div className="ins-tile-sub">{sub}</div>}
    <ScopeNote note={scopeNote ?? null} />
  </div>
);

type CardView = 'Chart' | 'Table';

const ChartCard: React.FC<{
  title: string;
  subtitle?: string;
  scopeNote?: string | null;
  extra?: React.ReactNode;
  tableColumns?: any[];
  tableData?: any[];
  hasData: boolean;
  emptyText?: string;
  children: React.ReactNode;
}> = ({ title, subtitle, scopeNote, extra, tableColumns, tableData, hasData, emptyText, children }) => {
  const [view, setView] = useState<CardView>('Chart');
  const canToggle = Boolean(tableColumns && tableData);

  return (
    <div className="ins-card">
      <div className="ins-card-head">
        <div>
          <div className="ins-card-title">{title}</div>
          {subtitle && <div className="ins-card-sub">{subtitle}</div>}
          <ScopeNote note={scopeNote ?? null} />
        </div>
        <Space size={8} wrap>
          {extra}
          {canToggle && (
            <Segmented
              size="small"
              value={view}
              onChange={(v) => setView(v as CardView)}
              options={['Chart', 'Table']}
            />
          )}
        </Space>
      </div>
      <div className="ins-card-body">
        {!hasData ? (
          <div className="ins-empty">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={emptyText || 'No records in this slice'}
            />
          </div>
        ) : view === 'Table' && canToggle ? (
          <div className="ins-table-scroll">
            <Table
              size="small"
              columns={tableColumns}
              dataSource={tableData}
              pagination={false}
              scroll={{ x: 460, y: 300 }}
              rowKey={(_r, i) => String(i)}
            />
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
};

// Max folded-bucket items listed in a hover tooltip; the rest are reached via click-to-search.
const TIP_OTHER_LIMIT = 10;

/** Tooltip: value leads, label follows; series keyed by a short stroke. */
const VizTooltip: React.FC<any> = ({ active, payload, label, rows }) => {
  if (!active || !payload || !payload.length) return null;
  const point = payload[0]?.payload || {};
  const otherItems: Array<{ awardType: string }> | undefined = point.otherItems;
  return (
    <div className="ins-tip">
      <div className="ins-tip-cat">{point.fullName || point.name || label}</div>
      {otherItems && (
        <>
          <ul className="ins-tip-list">
            {otherItems.slice(0, TIP_OTHER_LIMIT).map((o) => (
              <li key={o.awardType}>{o.awardType}</li>
            ))}
          </ul>
          <div className="ins-tip-hint">
            {otherItems.length > TIP_OTHER_LIMIT ? `+${otherItems.length - TIP_OTHER_LIMIT} more · ` : ''}
            Click the bar to search all
          </div>
        </>
      )}
      {(rows as Array<{ key: string; name: string; color: string; format: (v: any) => string }>).map((r) => (
        <div className="ins-tip-row" key={r.key}>
          <span className="ins-tip-key" style={{ background: r.color }} />
          <span className="ins-tip-value">{r.format(point[r.key])}</span>
          <span className="ins-tip-name">{r.name}</span>
        </div>
      ))}
    </div>
  );
};

/**
 * Horizontal magnitude bar. One series, one hue — bar length already encodes the
 * value, so a colour ramp across nominal categories would only double-encode it.
 * Height grows with the row count so the axis band is never cropped.
 * Rows are always drawn highest first: with layout="vertical" recharts puts the first
 * row at the TOP, so the component sorts here rather than trusting each caller. A row
 * flagged `pinLast` (e.g. a folded "Other" bucket) stays at the bottom regardless.
 */
const MagnitudeBars: React.FC<{
  data: Array<{ name: string; fullName: string; value: number; [k: string]: any }>;
  color?: string;
  valueFormat: (v: number) => string;
  axisFormat: (v: number) => string;
  tooltipRows: Array<{ key: string; name: string; color: string; format: (v: any) => string }>;
  labelWidth?: number;
  /** Called with the clicked row; only rows carrying `otherItems` are clickable. */
  onOtherClick?: (row: any) => void;
}> = ({ data, color = VIZ.series1, valueFormat, axisFormat, tooltipRows, labelWidth = 150, onOtherClick }) => {
  const rows = useMemo(
    () =>
      [...data].sort((a, b) =>
        Boolean(a.pinLast) === Boolean(b.pinLast) ? (b.value ?? 0) - (a.value ?? 0) : a.pinLast ? 1 : -1
      ),
    [data]
  );
  const height = Math.max(180, rows.length * 34 + 34);
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 72, left: 4, bottom: 4 }}>
        <CartesianGrid horizontal={false} stroke={VIZ.grid} strokeWidth={1} />
        <XAxis
          type="number"
          tickFormatter={axisFormat}
          tick={{ fill: VIZ.muted, fontSize: 11 }}
          axisLine={{ stroke: VIZ.axis }}
          tickLine={false}
        />
        <YAxis
          type="category"
          dataKey="name"
          width={labelWidth}
          tick={{ fill: VIZ.ink2, fontSize: 12 }}
          axisLine={false}
          tickLine={false}
        />
        <Tooltip
          cursor={{ fill: 'rgba(42,120,214,0.06)' }}
          content={<VizTooltip rows={tooltipRows} />}
        />
        <Bar
          dataKey="value"
          fill={color}
          barSize={18}
          radius={[0, 4, 4, 0]}
          isAnimationActive={false}
          onClick={(d: any) => {
            const row = d?.payload ?? d;
            if (onOtherClick && row?.otherItems) onOtherClick(row);
          }}
        >
          {onOtherClick &&
            rows.map((r, i) => (
              <Cell key={i} fill={color} cursor={(r as any).otherItems ? 'pointer' : 'default'} />
            ))}
          {/* Values ride the bar tips in ink, not the series colour — three of the
              light-mode series steps are illegible as text on white. */}
          <LabelList
            dataKey="value"
            position="right"
            formatter={(v: any) => valueFormat(Number(v))}
            fill={VIZ.ink2}
            fontSize={11}
            fontWeight={600}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

/* ─────────────────────────── the page ─────────────────────────── */

const currentYearStart = () => dayjs().startOf('year');
const currentYearEnd = () => dayjs().endOf('year');

const EMPTY_OPTIONS: FilterOptions = {
  countries: [],
  locations: [],
  awardTypes: [],
  awardTypeGroups: [],
  departments: [],
  businessUnits: [],
  managers: [],
  unitHeads: [],
};

// Department names arrive as "<cost-centre code> <name>" ("101 Sales"); the dropdown shows
// the name only. Several codes could share a name, so a label maps back to every original.
const departmentLabel = (d: string) => d.replace(/^\d{3}\s*/, '').trim() || d;

/**
 * Module-level cache of the last rendered view. The admin shell unmounts a tab's content
 * when you navigate away, so without this every return trip pays for a fresh token
 * acquisition plus a round trip behind a spinner. Coming back now paints instantly from
 * cache and revalidates in the background.
 */
/** Id of the slot in the admin shell's heading row that this page portals Export into. */
export const ADMIN_HEADER_ACTIONS_ID = 'admin-page-header-actions';

let viewCache: { key: string; data: DashboardPayload; options: FilterOptions } | null = null;

const cacheKey = (f: {
  countries: string[];
  locations: string[];
  departments: string[];
  businessUnits: string[];
  managerIds: string[];
  unitHeadIds: string[];
  rewardTypeIds: string[];
  fromDate: string;
  toDate: string;
}) =>
  JSON.stringify([
    f.fromDate,
    f.toDate,
    [...f.countries].sort(),
    [...f.locations].sort(),
    [...f.managerIds].sort(),
    [...f.rewardTypeIds].sort(),
    [...f.departments].sort(),
    [...f.businessUnits].sort(),
    [...f.unitHeadIds].sort(),
  ]);

const AwardsInsights: React.FC = () => {
  const { instance, accounts } = useMsal();

  const [options, setOptions] = useState<FilterOptions>(viewCache?.options ?? EMPTY_OPTIONS);
  const [data, setData] = useState<DashboardPayload | null>(viewCache?.data ?? null);
  const [initialLoading, setInitialLoading] = useState(!viewCache);
  const [refreshing, setRefreshing] = useState(false);

  // Draft filter state — nothing refetches until Apply, so the page never
  // flickers through half-built filter combinations.
  const [draftCountries, setDraftCountries] = useState<string[]>([]);
  const [draftLocations, setDraftLocations] = useState<string[]>([]);
  const [draftManagers, setDraftManagers] = useState<string[]>([]);
  const [draftUnitHeads, setDraftUnitHeads] = useState<string[]>([]);
  const [draftRewardTypes, setDraftRewardTypes] = useState<string[]>([]);
  const [draftDepartments, setDraftDepartments] = useState<string[]>([]);
  const [draftBusinessUnits, setDraftBusinessUnits] = useState<string[]>([]);
  const [draftFrom, setDraftFrom] = useState<Dayjs>(currentYearStart());
  const [draftTo, setDraftTo] = useState<Dayjs>(currentYearEnd());

  // Applied filters — what the current payload actually represents.
  const [applied, setApplied] = useState({
    countries: [] as string[],
    locations: [] as string[],
    managerIds: [] as string[],
    unitHeadIds: [] as string[],
    rewardTypeIds: [] as string[],
    departments: [] as string[],
    businessUnits: [] as string[],
    fromDate: currentYearStart().format('YYYY-MM-DD'),
    toDate: currentYearEnd().format('YYYY-MM-DD'),
  });

  // Export lives in the admin shell's heading row; the slot is rendered by AdminPages
  // before this page mounts, so it is found on the first effect pass.
  const [headerActions, setHeaderActions] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setHeaderActions(document.getElementById(ADMIN_HEADER_ACTIONS_ID));
  }, []);

  const [typeMetric, setTypeMetric] = useState<'Awards' | 'Value'>('Awards');
  // Searchable list behind the folded "Other" bar in Awards by type.
  const [otherTypes, setOtherTypes] = useState<Array<{ awardType: string; awards: number; amount: number; recipients: number }> | null>(null);
  const [otherSearch, setOtherSearch] = useState('');
  const [countryMetric, setCountryMetric] = useState<'Awards' | 'Value'>('Awards');

  // Guards the async setState calls in `load`, which also runs from event handlers that
  // have no effect cleanup of their own. It MUST be re-armed in the effect body: under
  // StrictMode React mounts, unmounts, then remounts, so a cleanup-only version would
  // leave this false on the mount that actually renders and the page would spin forever.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // Some figures genuinely cannot honour every filter: company budget is a single
  // org-wide total, GEM headroom is a point-in-time balance, and portal footfall is a
  // page visit with no award type or nominating manager attached. Rather than let those
  // cards sit still and read as broken, each one names the filters it ignores.
  const activeDimensions = useMemo(() => {
    const set = new Set<string>(['Date range']); // a range is always applied
    if (applied.countries.length) set.add('Country');
    if (applied.locations.length) set.add('Location');
    if (applied.managerIds.length) set.add('Manager');
    if (applied.unitHeadIds.length) set.add('Unit head');
    if (applied.rewardTypeIds.length) set.add('Award type');
    if (applied.departments.length) set.add("Department");
    if (applied.businessUnits.length) set.add("Business unit");
    return set;
  }, [applied]);

  const scopeNote = useCallback(
    (ignored: string[]) => {
      const hits = ignored.filter((d) => activeDimensions.has(d));
      if (!hits.length) return null;
      return `Not scoped by ${hits.join(' or ')}`;
    },
    [activeDimensions]
  );

  const managerNameById = useMemo(() => {
    const map = new Map<string, string>();
    options.managers.forEach((m) => map.set(m.id, m.name || m.email || m.id));
    return map;
  }, [options.managers]);

  // "Parent › Award" so a chip for "Cheers" says which Cheers it is.
  const rewardTypeLabelById = useMemo(() => {
    const map = new Map<string, string>();
    options.awardTypeGroups.forEach((g) => g.types.forEach((t) => map.set(t.id, `${g.parent} › ${t.name}`)));
    return map;
  }, [options.awardTypeGroups]);

  const departmentOriginals = useMemo(() => {
    const map = new Map<string, string[]>();
    options.departments.forEach((d) => {
      const label = departmentLabel(d);
      map.set(label, [...(map.get(label) || []), d]);
    });
    return map;
  }, [options.departments]);

  const unitHeadNameById = useMemo(() => {
    const map = new Map<string, string>();
    options.unitHeads.forEach((m) => map.set(m.id, m.name || m.email || m.id));
    return map;
  }, [options.unitHeads]);

  const load = useCallback(
    async (filters: typeof applied, opts?: { silent?: boolean; withFilters?: boolean }) => {
      const key = cacheKey(filters);
      if (!opts?.silent) setRefreshing(true);
      try {
        // One request carries both the aggregates and (on first paint) the filter lists,
        // so the page costs a single token acquisition and a single round trip.
        const res = await fetchInsightsDashboard(instance, accounts, {
          ...filters,
          // Filters hold the code-less department labels; the API matches the stored names.
          departments: filters.departments.flatMap((d) => departmentOriginals.get(d) || [d]),
          includeFilters: opts?.withFilters,
        });
        const payload = res?.apiResponse?.data as DashboardPayload | undefined;
        if (!mounted.current || !payload) return;

        setData(payload);
        const nextOptions = payload.filters
          ? {
              countries: payload.filters.countries || [],
              locations: payload.filters.locations || [],
              awardTypes: payload.filters.awardTypes || [],
              awardTypeGroups: payload.filters.awardTypeGroups || [],
              departments: payload.filters.departments || [],
              businessUnits: payload.filters.businessUnits || [],
              managers: payload.filters.managers || [],
              unitHeads: payload.filters.unitHeads || [],
            }
          : null;
        if (nextOptions) setOptions(nextOptions);
        viewCache = { key, data: payload, options: nextOptions ?? viewCache?.options ?? EMPTY_OPTIONS };
      } catch (err) {
        console.error('Insights dashboard load failed:', err);
        if (mounted.current && !opts?.silent) {
          message.error('Could not load dashboard data. Please try again.');
        }
      } finally {
        if (mounted.current) {
          setInitialLoading(false);
          setRefreshing(false);
        }
      }
    },
    [instance, accounts, departmentOriginals]
  );

  // First paint: current-year data, unfiltered. A warm cache paints immediately and this
  // revalidates quietly behind it; a cold one is the only case that shows a spinner.
  useEffect(() => {
    const warm = viewCache?.key === cacheKey(applied);
    load(applied, { silent: warm, withFilters: !viewCache?.options.countries.length || !viewCache?.options.unitHeads || !viewCache?.options.awardTypeGroups });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, accounts]);

  const handleApply = () => {
    if (!draftFrom || !draftTo) {
      message.warning('Pick both a from date and a to date.');
      return;
    }
    if (draftTo.isBefore(draftFrom, 'day')) {
      message.error('To Date cannot be before From Date.');
      return;
    }
    const next = {
      countries: draftCountries,
      locations: draftLocations,
      managerIds: draftManagers,
      unitHeadIds: draftUnitHeads,
      rewardTypeIds: draftRewardTypes,
      departments: draftDepartments,
      businessUnits: draftBusinessUnits,
      fromDate: draftFrom.format('YYYY-MM-DD'),
      toDate: draftTo.format('YYYY-MM-DD'),
    };
    setApplied(next);
    load(next);
  };

  /* ── CSV export of everything currently on screen ── */
  const exportCsv = () => {
    if (!data) return;
    const esc = (v: any) => `"${String(v ?? '').replace(/"/g, '""').replace(/\r?\n/g, ' ')}"`;
    const lines: string[] = [];
    const section = (title: string, headers: string[], rows: any[][]) => {
      lines.push(esc(title));
      lines.push(headers.map(esc).join(','));
      rows.forEach((r) => lines.push(r.map(esc).join(',')));
      lines.push('');
    };

    lines.push(esc('Aptean Thanks — Awards Insights'));
    lines.push(esc(`Date range: ${applied.fromDate} to ${applied.toDate}`));
    lines.push(esc(`Countries: ${applied.countries.join('; ') || 'All'}`));
    lines.push(esc(`Locations: ${applied.locations.join('; ') || 'All'}`));
    lines.push(
      esc(`Managers: ${applied.managerIds.map((id) => managerNameById.get(id) || id).join('; ') || 'All'}`)
    );
    lines.push(
      esc(`Unit heads: ${applied.unitHeadIds.map((id) => unitHeadNameById.get(id) || id).join('; ') || 'All'}`)
    );
    lines.push(
      esc(`Award types: ${applied.rewardTypeIds.map((id) => rewardTypeLabelById.get(id) || id).join('; ') || 'All'}`)
    );
    lines.push(esc(`Departments: ${applied.departments.join('; ') || 'All'}`));
    lines.push(esc(`Business units: ${applied.businessUnits.join('; ') || 'All'}`));
    lines.push('');

    section(
      'Summary',
      ['Metric', 'Value'],
      [
        ['Total awards', data.summary.totalAwards],
        ['Awarded', data.summary.awardedAwards],
        ['Pending', data.summary.pendingAwards],
        ['Rejected', data.summary.rejectedAwards],
        ['Unique recipients', data.summary.uniqueRecipients],
        ['Managers who nominated', data.summary.uniqueManagers],
        ['Countries covered', data.summary.countriesCovered],
        ['Used budget — filtered award value ($)', data.summary.filteredSpend],
        ['Average awarded value ($)', data.summary.averageAwardValue],
        ['Approval rate (%)', data.summary.approvalRate],
        ['Company total budget ($)', data.budget.totalBudget],
        ['Company used budget ($)', data.budget.usedBudget],
        ['Company remaining budget ($)', data.budget.balance],
        ['GEM awards allocated', data.gem.allocated],
        ['GEM awards used', data.gem.used],
        ['GEM awards remaining', data.gem.remaining],
        ['Footfall visits', data.summary.footfallVisits],
        ['Footfall unique visitors', data.summary.footfallUniqueVisitors],
      ]
    );

    section(
      'Awards by type',
      ['Award type', 'Awards', 'Value ($)', 'Recipients'],
      data.awardsByType.map((r) => [r.awardType, r.awards, r.amount, r.recipients])
    );
    section(
      'Approval status',
      ['Status', 'Awards', 'Value ($)'],
      data.approvalStatus.map((r) => [r.status, r.awards, r.amount])
    );
    section(
      'Top managers by budget used',
      ['Manager', 'Email', 'Country', 'Used in view ($)', 'Awards', 'Recipients', 'Allocated budget ($)', 'Allocated balance ($)'],
      data.topManagers.map((r) => [
        r.managerName, r.managerEmail, r.country, r.usedBudget, r.awards, r.recipients, r.allocatedBudget, r.allocatedBalance,
      ])
    );
    section(
      'Top countries',
      ['Country', 'Awards', 'Value ($)', 'Recipients'],
      data.topCountries.map((r) => [r.country, r.awards, r.amount, r.recipients])
    );
    section(
      'Top locations',
      ['Location', 'Awards', 'Value ($)', 'Recipients'],
      data.topLocations.map((r) => [r.location, r.awards, r.amount, r.recipients])
    );
    section(
      'Top departments',
      ['Department', 'Awards', 'Value ($)', 'Recipients'],
      data.topDepartments.map((r) => [r.department, r.awards, r.amount, r.recipients])
    );
    section(
      'Awards by business unit',
      ['Business unit', 'Awards', 'Value ($)', 'Recipients'],
      data.awardsByBusinessUnit.map((r) => [r.businessUnit, r.awards, r.amount, r.recipients])
    );
    section(
      'Manager budget (allocation is real; recorded-used is not maintained in this system)',
      ['Metric', 'Value'],
      [
        ['Managers in scope', data.managerBudget.managers],
        ['Allocated ($)', data.managerBudget.allocated],
        ['Balance ($)', data.managerBudget.balance],
        ['Recorded used ($)', data.managerBudget.recordedUsed],
        ['Recorded-used ledger unpopulated', data.managerBudget.recordedUsedIsUnpopulated ? 'YES' : 'no'],
      ]
    );
    section(
      'GEM balance by unit head',
      ['Unit head', 'Country', 'Location', 'Allocated', 'Used', 'Remaining'],
      data.gem.byUnitHead.map((r) => [r.unitHeadName, r.country, r.location, r.allocated, r.used, r.remaining])
    );
    section(
      'Top recipients',
      ['Recipient', 'Email', 'Country', 'Awards', 'Value ($)'],
      data.topRecipients.map((r) => [r.nomineeName, r.nomineeEmail, r.country, r.awards, r.amount])
    );
    section(
      'Footfall trend',
      ['Period', 'Unique visitors', 'Visits'],
      data.footfallTrend.map((r) => [r.label, r.uniqueVisitors, r.visits])
    );
    section(
      'Award trend',
      ['Period', 'Awards', 'Value ($)'],
      data.awardTrend.map((r) => [r.label, r.awards, r.amount])
    );

    // Explicit BOM so Excel opens the export as UTF-8.
    const blob = new Blob([`\uFEFF${lines.join('\n')}`], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Awards_Insights_${applied.fromDate}_to_${applied.toDate}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    message.success('Dashboard exported.');
  };

  /* ── derived chart data ── */
  const typeChart = useMemo(() => {
    if (!data) return [];
    const key = typeMetric === 'Awards' ? 'awards' : 'amount';
    const sorted = [...data.awardsByType].sort((a, b) => (b as any)[key] - (a as any)[key]);
    // Past ~10 rows a bar chart stops being readable; the tail folds into one row
    // rather than being silently dropped.
    const head = sorted.slice(0, 10);
    const tail = sorted.slice(10);
    const rows: Array<{ name: string; fullName: string; value: number; [k: string]: any }> = head.map((r) => ({
      name: truncate(r.awardType, 24),
      fullName: r.awardType,
      value: (r as any)[key],
      awards: r.awards,
      amount: r.amount,
      recipients: r.recipients,
    }));
    if (tail.length) {
      rows.push({
        name: `Other (${tail.length})`,
        fullName: `Other award types (${tail.length})`,
        // Listed one per line in the tooltip; clicking the bar opens a searchable list.
        otherItems: tail.map((t) => ({ awardType: t.awardType, awards: t.awards, amount: t.amount, recipients: t.recipients })),
        value: tail.reduce((s, r) => s + ((r as any)[key] as number), 0),
        awards: tail.reduce((s, r) => s + r.awards, 0),
        amount: tail.reduce((s, r) => s + r.amount, 0),
        recipients: tail.reduce((s, r) => s + r.recipients, 0),
        pinLast: true,
      });
    }
    return rows;
  }, [data, typeMetric]);

  const countryChart = useMemo(() => {
    if (!data) return [];
    const key = countryMetric === 'Awards' ? 'awards' : 'amount';
    return [...data.topCountries]
      .sort((a, b) => (b as any)[key] - (a as any)[key])
      .map((r) => ({
        name: truncate(r.country, 20),
        fullName: r.country,
        value: (r as any)[key],
        awards: r.awards,
        amount: r.amount,
        recipients: r.recipients,
      }));
  }, [data, countryMetric]);

  const managerChart = useMemo(() => {
    if (!data) return [];
    return [...data.topManagers]
      .sort((a, b) => b.usedBudget - a.usedBudget)
      .map((r) => ({
        name: truncate(r.managerName, 22),
        fullName: `${r.managerName}${r.managerEmail ? ` · ${r.managerEmail}` : ''}`,
        value: r.usedBudget,
        awards: r.awards,
        recipients: r.recipients,
        allocatedBalance: r.allocatedBalance,
      }));
  }, [data]);

  const statusChart = useMemo(
    () =>
      data ? data.approvalStatus.filter((s) => s.awards > 0).sort((a, b) => b.awards - a.awards) : [],
    [data]
  );
  const statusTotal = useMemo(() => statusChart.reduce((s, r) => s + r.awards, 0), [statusChart]);

  if (initialLoading) {
    return (
      <div style={{ padding: 60, textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: 14, color: VIZ.muted, fontSize: 13 }}>Building your dashboard…</div>
      </div>
    );
  }

  const summary = data?.summary;
  const budget = data?.budget;
  const gem = data?.gem;
  const granularityLabel =
    data?.range.granularity === 'day' ? 'day' : data?.range.granularity === 'week' ? 'week' : 'month';
  const rangeLabel = `${dayjs(applied.fromDate).format('DD MMM YYYY')} – ${dayjs(applied.toDate).format('DD MMM YYYY')}`;

  const activeChips: Array<{ label: string; onClose: () => void }> = [
    ...applied.countries.map((c) => ({
      label: `Country: ${c}`,
      onClose: () => {
        const next = { ...applied, countries: applied.countries.filter((x) => x !== c) };
        setDraftCountries(next.countries);
        setApplied(next);
        load(next);
      },
    })),
    ...applied.locations.map((c) => ({
      label: `Location: ${c}`,
      onClose: () => {
        const next = { ...applied, locations: applied.locations.filter((x) => x !== c) };
        setDraftLocations(next.locations);
        setApplied(next);
        load(next);
      },
    })),
    ...applied.managerIds.map((id) => ({
      label: `Manager: ${managerNameById.get(id) || id}`,
      onClose: () => {
        const next = { ...applied, managerIds: applied.managerIds.filter((x) => x !== id) };
        setDraftManagers(next.managerIds);
        setApplied(next);
        load(next);
      },
    })),
    ...applied.unitHeadIds.map((id) => ({
      label: `Unit head: ${unitHeadNameById.get(id) || id}`,
      onClose: () => {
        const next = { ...applied, unitHeadIds: applied.unitHeadIds.filter((x) => x !== id) };
        setDraftUnitHeads(next.unitHeadIds);
        setApplied(next);
        load(next);
      },
    })),
    ...applied.rewardTypeIds.map((id) => ({
      label: `Award type: ${rewardTypeLabelById.get(id) || id}`,
      onClose: () => {
        const next = { ...applied, rewardTypeIds: applied.rewardTypeIds.filter((x) => x !== id) };
        setDraftRewardTypes(next.rewardTypeIds);
        setApplied(next);
        load(next);
      },
    })),
    ...applied.departments.map((c) => ({
      label: `Department: ${c}`,
      onClose: () => {
        const next = { ...applied, departments: applied.departments.filter((x) => x !== c) };
        setDraftDepartments(next.departments);
        setApplied(next);
        load(next);
      },
    })),
    ...applied.businessUnits.map((c) => ({
      label: `Business unit: ${c}`,
      onClose: () => {
        const next = { ...applied, businessUnits: applied.businessUnits.filter((x) => x !== c) };
        setDraftBusinessUnits(next.businessUnits);
        setApplied(next);
        load(next);
      },
    })),
  ];

  return (
    <div className="ins-root">
      {headerActions &&
        createPortal(
          <Button icon={<DownloadOutlined />} onClick={exportCsv} disabled={!data}>
            Export
          </Button>,
          headerActions
        )}
      {/* ── One filter row, above everything it scopes ── */}
      <div className="ins-filters">
        <Row gutter={[12, 12]} align="bottom">
          <Col xs={24} sm={12} md={8} lg={4}>
            <span className="ins-filter-label">Country</span>
            <Select
              mode="multiple"
              allowClear
              showSearch
              maxTagCount="responsive"
              placeholder="Select countries"
              style={{ width: '100%' }}
              value={draftCountries}
              onChange={setDraftCountries}
              options={options.countries.map((c) => ({ label: c, value: c }))}
              filterOption={(input, option) =>
                String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <span className="ins-filter-label">Location</span>
            <Select
              mode="multiple"
              allowClear
              showSearch
              maxTagCount="responsive"
              placeholder="Select locations"
              style={{ width: '100%' }}
              value={draftLocations}
              onChange={setDraftLocations}
              options={options.locations.map((c) => ({ label: c, value: c }))}
              filterOption={(input, option) =>
                String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <span className="ins-filter-label">Manager</span>
            <Select
              mode="multiple"
              allowClear
              showSearch
              maxTagCount="responsive"
              placeholder="Select managers"
              style={{ width: '100%' }}
              value={draftManagers}
              onChange={setDraftManagers}
              options={options.managers.map((m) => ({
                label: m.name || m.email,
                value: m.id,
              }))}
              filterOption={(input, option) =>
                String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <span className="ins-filter-label">Unit head</span>
            <Select
              mode="multiple"
              allowClear
              showSearch
              maxTagCount="responsive"
              placeholder="Select unit heads"
              style={{ width: '100%' }}
              value={draftUnitHeads}
              onChange={setDraftUnitHeads}
              options={options.unitHeads.map((m) => ({
                label: m.name || m.email,
                value: m.id,
              }))}
              filterOption={(input, option) =>
                String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <span className="ins-filter-label">Award type</span>
            <Select
              mode="multiple"
              allowClear
              showSearch
              maxTagCount="responsive"
              placeholder="Select award types"
              style={{ width: '100%' }}
              value={draftRewardTypes}
              onChange={setDraftRewardTypes}
              options={options.awardTypeGroups.map((g) => ({
                label: g.parent,
                title: g.parent,
                options: g.types.map((t) => ({ label: t.name, value: t.id })),
              }))}
              filterOption={(input, option) =>
                String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <span className="ins-filter-label">Department</span>
            <Select
              mode="multiple"
              allowClear
              showSearch
              maxTagCount="responsive"
              placeholder="Select departments"
              style={{ width: '100%' }}
              value={draftDepartments}
              onChange={setDraftDepartments}
              options={Array.from(departmentOriginals.keys())
                .sort((a, b) => a.localeCompare(b))
                .map((label) => ({ label, value: label }))}
              filterOption={(input, option) =>
                String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <span className="ins-filter-label">Business unit</span>
            <Select
              mode="multiple"
              allowClear
              showSearch
              maxTagCount="responsive"
              placeholder="Select business units"
              style={{ width: '100%' }}
              value={draftBusinessUnits}
              onChange={setDraftBusinessUnits}
              options={options.businessUnits.map((c) => ({ label: c, value: c }))}
              filterOption={(input, option) =>
                String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Col>
          <Col xs={12} sm={8} md={6} lg={4}>
            <span className="ins-filter-label">From date</span>
            <DatePicker
              style={{ width: '100%' }}
              format="DD MMM YYYY"
              allowClear={false}
              value={draftFrom}
              onChange={(d) => {
                if (d) setDraftFrom(d);
              }}
            />
          </Col>
          <Col xs={12} sm={8} md={6} lg={4}>
            <span className="ins-filter-label">To date</span>
            <DatePicker
              style={{ width: '100%' }}
              format="DD MMM YYYY"
              allowClear={false}
              value={draftTo}
              disabledDate={(d) => (draftFrom ? d.isBefore(draftFrom, 'day') : false)}
              onChange={(d) => {
                if (d) setDraftTo(d);
              }}
            />
          </Col>
          <Col xs={24} sm={8} md={12} lg={8}>
            <Space wrap>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleApply}
                loading={refreshing}
                style={{ background: VIZ.brand, borderColor: VIZ.brand }}
              >
                Apply
              </Button>
            </Space>
          </Col>
        </Row>

        {activeChips.length > 0 && (
          <div className="ins-active-chip-row">
            <span style={{ fontSize: 11, color: VIZ.muted, fontWeight: 600 }}>ACTIVE FILTERS</span>
            {activeChips.map((chip) => (
              <Tag key={chip.label} closable onClose={chip.onClose} color="purple">
                {chip.label}
              </Tag>
            ))}
          </div>
        )}
      </div>

      <div className={refreshing ? 'ins-refreshing' : undefined}>
        {/* Tiles are grouped by SCOPE, not by topic. Mixing a filtered figure and an
            unfiltered one in the same tile (or the same unlabelled row) is what made the
            company "Used budget" look like it was responding to filters. */}
        <div className="ins-row-scope-label is-filtered">
          Responds to your filters · {rangeLabel}
        </div>
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={12} lg={8}>
            <StatTile
              hero
              label="Total awards"
              icon={<TrophyOutlined />}
              accent={VIZ.brand}
              value={num(summary?.totalAwards)}
              sub={`Across ${num(summary?.awardTypesInPlay)} award types · ${num(
                summary?.uniqueRecipients
              )} recipients · ${num(summary?.countriesCovered)} countries`}
            />
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <StatTile
              label="Approval rate"
              accent={VIZ.series3}
              value={`${summary?.approvalRate ?? 0}%`}
              sub={`${num(summary?.awardedAwards)} awarded · ${num(
                summary?.pendingAwards
              )} pending · ${num(summary?.rejectedAwards)} rejected`}
            />
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <StatTile
              label="Portal footfall"
              icon={<GlobalOutlined />}
              accent={VIZ.series2}
              value={num(summary?.footfallUniqueVisitors)}
              sub={`${num(summary?.footfallVisits)} visits in range`}
              scopeNote={scopeNote(['Manager', 'Unit head', 'Award type'])}
            />
          </Col>
        </Row>

        {/* Five tiles share the row evenly and wrap on narrow screens. */}
        <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
          <Col flex="1 1 170px">
            <StatTile
              label="Pending"
              accent={VIZ.warning}
              value={num(summary?.pendingAwards)}
              sub="Waiting on an approver"
            />
          </Col>
          <Col flex="1 1 170px">
            <StatTile
              label="Rejected"
              accent={VIZ.critical}
              value={num(summary?.rejectedAwards)}
              sub="Declined at some level"
            />
          </Col>
          <Col flex="1 1 170px">
            <StatTile
              label="Recipients"
              accent={VIZ.series3}
              value={num(summary?.uniqueRecipients)}
              sub="Distinct people rewarded"
            />
          </Col>
          <Col flex="1 1 170px">
            <StatTile
              label="Award types in play"
              icon={<GiftOutlined />}
              accent={VIZ.series1}
              value={num(summary?.awardTypesInPlay)}
              sub="Types with at least one award"
            />
          </Col>
          <Col flex="1 1 170px">
            <StatTile
              label="Managers nominating"
              icon={<TeamOutlined />}
              accent={VIZ.series1}
              value={num(summary?.uniqueManagers)}
              sub="Distinct budget spenders"
            />
          </Col>
        </Row>

        {/* All four budget figures sit on one row, 4 x lg=6 = 24 columns so they never
            wrap. They do NOT share a filter scope — Used budget is filtered, Company is
            not, Manager/GEM are partly — so each tile keeps its own scope note. */}
        <div className="ins-row-scope-label is-static">
          Budget &amp; balances · each tile states which filters apply to it
        </div>
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={12} lg={6}>
            <StatTile
              label="Company budget"
              icon={<DollarOutlined />}
              accent={VIZ.series1}
              value={usd(budget?.totalBudget)}
              meterPct={budget?.utilizationPct ?? 0}
              hint="The active company budget cycle as maintained on the admin Budget screen: allocated, recorded-used and remaining. These are entered figures with no country/manager/award-type breakdown, so no filter narrows them."
              sub={`${usd(budget?.usedBudget)} recorded used · ${usd(
                budget?.balance
              )} left · ${budget?.utilizationPct ?? 0}% utilised`}
              scopeNote={scopeNote(['Date range', 'Country', 'Location', 'Manager', 'Unit head', 'Award type'])}
            />
          </Col>
          {/* The FILTERED award value, from the point transactions behind the awards — the
              same source as every other figure here, so the page reconciles with itself.
              Deliberately not mainbudgets.used_budget, which is hand-entered and has no
              country/manager/award-type breakdown to filter by. */}
          <Col xs={24} sm={12} lg={6}>
            <StatTile
              label="Used budget"
              icon={<WalletOutlined />}
              accent={VIZ.series2}
              value={usd(summary?.filteredSpend)}
              hint={`Award value inside the current filters: ${usdExact(summary?.filteredSpend)}. Summed from the points actually awarded, so it moves with every filter and ties back to Total awards, Awards by type and Top managers.`}
              sub={`Filtered spend · avg ${usdExact(summary?.averageAwardValue)} per award`}
            />
          </Col>
          {/* Manager budget allocation and balance are genuine ($6,655 across 245 managers,
              balance consistent on every row). used_budget is 0 on every row because the
              award flow charges MainBudget on the admin path, so it is never shown as spend. */}
          <Col xs={24} sm={12} lg={6}>
            <StatTile
              label="Manager budget"
              icon={<TeamOutlined />}
              accent={VIZ.series3}
              value={usd(data?.managerBudget.allocated)}
              hint="Sum of manager budget allocations for the managers in scope, with the remaining balance. Scoped by Manager and by the manager's own country/location; a date range cannot narrow a running balance."
              sub={`allocated · ${usd(data?.managerBudget.balance)} balance across ${num(
                data?.managerBudget.managers
              )} managers`}
              scopeNote={
                data?.managerBudget.recordedUsedIsUnpopulated
                  ? "Recorded 'used' is 0 on every manager budget — Used budget is the real spend"
                  : scopeNote(['Date range', 'Unit head', 'Award type'])
              }
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatTile
              label="GEM awards remaining"
              icon={<GiftOutlined />}
              accent={(gem?.remaining ?? 0) > 0 ? VIZ.good : VIZ.critical}
              value={num(gem?.remaining)}
              meterPct={gem?.utilizationPct ?? 0}
              hint="Sum of unit-head available balance (allocated minus used) across unit budgets. A point-in-time balance, so a date range cannot narrow it; Country/Location/Manager do scope it via the unit head."
              sub={`${num(gem?.used)} of ${num(gem?.allocated)} used · ${num(
                gem?.unitHeadsExhausted
              )} of ${num(gem?.unitHeads)} unit heads exhausted`}
              scopeNote={scopeNote(['Date range', 'Award type'])}
            />
          </Col>
        </Row>

        {/* ── Charts, in the order the business reads them. Two per row on desktop;
            the award trends (not in the requested list) close out the grid. ── */}
        <Row gutter={[12, 12]} style={{ marginTop: 16 }}>
          <Col xs={24} lg={12}>
            <ChartCard
              title="Footfall trend"
              subtitle={`Portal visitors per ${granularityLabel}. Unique visitors and total visits are both counts, plotted on one axis.`}
              scopeNote={scopeNote(['Manager', 'Unit head', 'Award type'])}
              hasData={(data?.footfallTrend?.length ?? 0) > 0}
              tableColumns={[
                { title: 'Period', dataIndex: 'label', key: 'label' },
                {
                  title: 'Unique visitors',
                  dataIndex: 'uniqueVisitors',
                  key: 'uniqueVisitors',
                  align: 'right' as const,
                  className: 'ins-num',
                  width: 100,
                  render: (v: number) => num(v),
                },
                {
                  title: 'Visits',
                  dataIndex: 'visits',
                  key: 'visits',
                  align: 'right' as const,
                  className: 'ins-num',
                  width: 100,
                  render: (v: number) => num(v),
                },
              ]}
              tableData={data?.footfallTrend || []}
            >
              {/* Two series → a legend is always present. */}
              <div style={{ display: 'flex', gap: 18, padding: '0 6px 6px 8px' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: VIZ.ink2 }}>
                  <span style={{ width: 14, height: 2, background: VIZ.series1, borderRadius: 2 }} />
                  Unique visitors
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: VIZ.ink2 }}>
                  <span style={{ width: 14, height: 2, background: VIZ.series2, borderRadius: 2 }} />
                  Total visits
                </span>
              </div>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={data?.footfallTrend || []} margin={{ top: 12, right: 34, left: 0, bottom: 6 }}>
                  <CartesianGrid vertical={false} stroke={VIZ.grid} strokeWidth={1} />
                  <XAxis
                    dataKey="label"
                    tick={{ fill: VIZ.muted, fontSize: 11 }}
                    axisLine={{ stroke: VIZ.axis }}
                    tickLine={false}
                    minTickGap={18}
                  />
                  <YAxis
                    tick={{ fill: VIZ.muted, fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => compactNum(Number(v))}
                  />
                  <Tooltip
                    cursor={{ stroke: VIZ.axis, strokeWidth: 1 }}
                    content={
                      <VizTooltip
                        rows={[
                          { key: 'uniqueVisitors', name: 'unique visitors', color: VIZ.series1, format: (v: any) => num(v) },
                          { key: 'visits', name: 'total visits', color: VIZ.series2, format: (v: any) => num(v) },
                        ]}
                      />
                    }
                  />
                  <Line
                    type="monotone"
                    dataKey="uniqueVisitors"
                    stroke={VIZ.series1}
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    dot={false}
                    activeDot={{ r: 4, fill: VIZ.series1, stroke: VIZ.surface, strokeWidth: 2 }}
                    isAnimationActive={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="visits"
                    stroke={VIZ.series2}
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    dot={false}
                    activeDot={{ r: 4, fill: VIZ.series2, stroke: VIZ.surface, strokeWidth: 2 }}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>
          </Col>

          <Col xs={24} lg={12}>
            <ChartCard
              title="Awards by type"
              subtitle={`${typeMetric === 'Awards' ? 'Award count' : 'Award value in $'} per reward type · ${rangeLabel}`}
              hasData={typeChart.length > 0}
              extra={
                <Segmented
                  size="small"
                  value={typeMetric}
                  onChange={(v) => setTypeMetric(v as 'Awards' | 'Value')}
                  options={['Awards', 'Value']}
                />
              }
              tableColumns={[
                { title: 'Award type', dataIndex: 'awardType', key: 'awardType' },
                {
                  title: 'Awards',
                  dataIndex: 'awards',
                  key: 'awards',
                  align: 'right' as const,
                  className: 'ins-num',
                  width: 100,
                  sorter: (a: any, b: any) => a.awards - b.awards,
                  render: (v: number) => num(v),
                },
                {
                  title: 'Value',
                  dataIndex: 'amount',
                  key: 'amount',
                  align: 'right' as const,
                  className: 'ins-num',
                  width: 100,
                  sorter: (a: any, b: any) => a.amount - b.amount,
                  render: (v: number) => usdExact(v),
                },
                {
                  title: 'Recipients',
                  dataIndex: 'recipients',
                  key: 'recipients',
                  align: 'right' as const,
                  className: 'ins-num',
                  width: 100,
                  render: (v: number) => num(v),
                },
              ]}
              tableData={[...(data?.awardsByType || [])].sort((a, b) =>
                typeMetric === 'Awards' ? b.awards - a.awards : b.amount - a.amount
              )}
            >
              <MagnitudeBars
                data={typeChart}
                valueFormat={(v) => (typeMetric === 'Awards' ? num(v) : compactUsd(v))}
                axisFormat={(v) => (typeMetric === 'Awards' ? compactNum(v) : compactUsd(v))}
                tooltipRows={[
                  { key: 'awards', name: 'awards', color: VIZ.series1, format: (v) => num(v) },
                  { key: 'amount', name: 'value', color: VIZ.series2, format: (v) => usdExact(v) },
                  { key: 'recipients', name: 'recipients', color: VIZ.series3, format: (v) => num(v) },
                ]}
                labelWidth={165}
                onOtherClick={(row) => {
                  setOtherSearch('');
                  setOtherTypes(row.otherItems);
                }}
              />
            </ChartCard>
            <Modal
              open={!!otherTypes}
              onCancel={() => setOtherTypes(null)}
              footer={null}
              width={560}
              title={`Other award types (${otherTypes?.length ?? 0})`}
            >
              <Input.Search
                placeholder="Search award type"
                allowClear
                value={otherSearch}
                onChange={(e) => setOtherSearch(e.target.value)}
                style={{ marginBottom: 12 }}
                autoFocus
              />
              <Table
                size="small"
                rowKey="awardType"
                pagination={false}
                scroll={{ y: 360 }}
                dataSource={(otherTypes || []).filter((o) =>
                  o.awardType.toLowerCase().includes(otherSearch.trim().toLowerCase())
                )}
                locale={{ emptyText: 'No matching award types' }}
                columns={[
                  { title: 'Award type', dataIndex: 'awardType', key: 'awardType' },
                  {
                    title: 'Awards',
                    dataIndex: 'awards',
                    key: 'awards',
                    align: 'right' as const,
                    width: 80,
                    sorter: (a: any, b: any) => a.awards - b.awards,
                    render: (v: number) => num(v),
                  },
                  {
                    title: 'Value',
                    dataIndex: 'amount',
                    key: 'amount',
                    align: 'right' as const,
                    width: 100,
                    sorter: (a: any, b: any) => a.amount - b.amount,
                    render: (v: number) => usdExact(v),
                  },
                ]}
              />
            </Modal>
          </Col>

          <Col xs={24} lg={12}>
            <ChartCard
              title="Top 10 countries"
              subtitle={`Ranked by ${countryMetric === 'Awards' ? 'award count' : 'award value'}`}
              hasData={countryChart.length > 0}
              extra={
                <Segmented
                  size="small"
                  value={countryMetric}
                  onChange={(v) => setCountryMetric(v as 'Awards' | 'Value')}
                  options={['Awards', 'Value']}
                />
              }
              tableColumns={[
                { title: 'Country', dataIndex: 'country', key: 'country' },
                {
                  title: 'Awards',
                  dataIndex: 'awards',
                  key: 'awards',
                  align: 'right' as const,
                  className: 'ins-num',
                  width: 100,
                  sorter: (a: any, b: any) => a.awards - b.awards,
                  render: (v: number) => num(v),
                },
                {
                  title: 'Value',
                  dataIndex: 'amount',
                  key: 'amount',
                  align: 'right' as const,
                  className: 'ins-num',
                  width: 100,
                  sorter: (a: any, b: any) => a.amount - b.amount,
                  render: (v: number) => usdExact(v),
                },
                {
                  title: 'Recipients',
                  dataIndex: 'recipients',
                  key: 'recipients',
                  align: 'right' as const,
                  className: 'ins-num',
                  width: 100,
                  render: (v: number) => num(v),
                },
              ]}
              tableData={[...(data?.topCountries || [])].sort((a, b) =>
                countryMetric === 'Awards' ? b.awards - a.awards : b.amount - a.amount
              )}
            >
              <MagnitudeBars
                data={countryChart}
                valueFormat={(v) => (countryMetric === 'Awards' ? num(v) : compactUsd(v))}
                axisFormat={(v) => (countryMetric === 'Awards' ? compactNum(v) : compactUsd(v))}
                tooltipRows={[
                  { key: 'awards', name: 'awards', color: VIZ.series1, format: (v) => num(v) },
                  { key: 'amount', name: 'value', color: VIZ.series2, format: (v) => usdExact(v) },
                  { key: 'recipients', name: 'recipients', color: VIZ.series3, format: (v) => num(v) },
                ]}
                labelWidth={130}
              />
            </ChartCard>
          </Col>

          <Col xs={24} lg={12}>
            <ChartCard
              title="Top locations"
              subtitle="Office locations by award count"
              hasData={(data?.topLocations?.length ?? 0) > 0}
              tableColumns={[
                { title: 'Location', dataIndex: 'location', key: 'location' },
                {
                  title: 'Awards',
                  dataIndex: 'awards',
                  key: 'awards',
                  align: 'right' as const,
                  className: 'ins-num',
                  width: 100,
                  render: (v: number) => num(v),
                },
                {
                  title: 'Value',
                  dataIndex: 'amount',
                  key: 'amount',
                  align: 'right' as const,
                  className: 'ins-num',
                  width: 100,
                  render: (v: number) => usdExact(v),
                },
              ]}
              tableData={data?.topLocations || []}
            >
              <MagnitudeBars
                data={(data?.topLocations || [])
                  .map((r) => ({
                    name: truncate(r.location, 20),
                    fullName: r.location,
                    value: r.awards,
                    awards: r.awards,
                    amount: r.amount,
                    recipients: r.recipients,
                  }))}
                valueFormat={(v) => num(v)}
                axisFormat={(v) => compactNum(v)}
                tooltipRows={[
                  { key: 'awards', name: 'awards', color: VIZ.series1, format: (v) => num(v) },
                  { key: 'amount', name: 'value', color: VIZ.series2, format: (v) => usdExact(v) },
                ]}
                labelWidth={130}
              />
            </ChartCard>
          </Col>

          <Col xs={24} lg={12}>
            <ChartCard
              title="Top 10 managers by budget used"
              subtitle="Award value nominated by each manager, highest first"
              hasData={managerChart.length > 0}
              tableColumns={[
                { title: 'Manager', dataIndex: 'managerName', key: 'managerName' },
                { title: 'Email', dataIndex: 'managerEmail', key: 'managerEmail' },
                { title: 'Country', dataIndex: 'country', key: 'country' },
                {
                  title: 'Used in view',
                  dataIndex: 'usedBudget',
                  key: 'usedBudget',
                  align: 'right' as const,
                  className: 'ins-num',
                  width: 100,
                  sorter: (a: any, b: any) => a.usedBudget - b.usedBudget,
                  render: (v: number) => usdExact(v),
                },
                {
                  title: 'Awards',
                  dataIndex: 'awards',
                  key: 'awards',
                  align: 'right' as const,
                  className: 'ins-num',
                  width: 100,
                  render: (v: number) => num(v),
                },
                {
                  title: 'Allocated budget',
                  dataIndex: 'allocatedBudget',
                  key: 'allocatedBudget',
                  align: 'right' as const,
                  className: 'ins-num',
                  width: 100,
                  render: (v: number) => usdExact(v),
                },
                {
                  title: 'Allocated balance',
                  dataIndex: 'allocatedBalance',
                  key: 'allocatedBalance',
                  align: 'right' as const,
                  className: 'ins-num',
                  width: 100,
                  render: (v: number) => usdExact(v),
                },
              ]}
              tableData={data?.topManagers || []}
            >
              <MagnitudeBars
                data={managerChart}
                valueFormat={(v) => compactUsd(v)}
                axisFormat={(v) => compactUsd(v)}
                tooltipRows={[
                  { key: 'value', name: 'budget used', color: VIZ.series1, format: (v) => usdExact(v) },
                  { key: 'awards', name: 'awards given', color: VIZ.series2, format: (v) => num(v) },
                  { key: 'recipients', name: 'recipients', color: VIZ.series3, format: (v) => num(v) },
                ]}
                labelWidth={150}
              />
            </ChartCard>
          </Col>

          <Col xs={24} lg={12}>
            <div className="ins-card">
              <div className="ins-card-head">
                <div>
                  <div className="ins-card-title">GEM Unit Head Budget</div>
                  <div className="ins-card-sub">
                    Available balance driving the remaining-GEM figure · highest remaining first
                  </div>
                </div>
              </div>
              <div className="ins-card-body ins-table-scroll">
                <Table
                  size="small"
                  rowKey="unitHeadUserId"
                  pagination={false}
                  scroll={{ y: 280 }}
                  dataSource={data?.gem.byUnitHead || []}
                  locale={{ emptyText: 'No unit budgets in this slice' }}
                  columns={[
                    { title: 'Unit head', dataIndex: 'unitHeadName', key: 'unitHeadName' },
                    { title: 'Country', dataIndex: 'country', key: 'country' },
                    {
                      title: 'Allocated',
                      dataIndex: 'allocated',
                      key: 'allocated',
                      align: 'right' as const,
                      className: 'ins-num',
                      width: 75,
                      render: (v: number) => num(v),
                    },
                    {
                      title: 'Used',
                      dataIndex: 'used',
                      key: 'used',
                      align: 'right' as const,
                      className: 'ins-num',
                      width: 75,
                      render: (v: number) => num(v),
                    },
                    {
                      title: 'Remaining',
                      dataIndex: 'remaining',
                      key: 'remaining',
                      align: 'right' as const,
                      className: 'ins-num',
                  width: 100,
                      sorter: (a: any, b: any) => a.remaining - b.remaining,
                      render: (v: number) => (
                        <span style={{ fontWeight: 700, color: v > 0 ? VIZ.ink : VIZ.critical }}>
                          {num(v)}
                        </span>
                      ),
                    },
                  ]}
                />
              </div>
            </div>
          </Col>

          <Col xs={24} lg={12}>
            <ChartCard
              title="Awards by business unit"
              subtitle="Recipient business unit, from the HCM employee master"
              hasData={(data?.awardsByBusinessUnit?.length ?? 0) > 0}
              tableColumns={[
                { title: 'Business unit', dataIndex: 'businessUnit', key: 'businessUnit' },
                {
                  title: 'Awards',
                  dataIndex: 'awards',
                  key: 'awards',
                  align: 'right' as const,
                  className: 'ins-num',
                  width: 100,
                  sorter: (a: any, b: any) => a.awards - b.awards,
                  render: (v: number) => num(v),
                },
                {
                  title: 'Value',
                  dataIndex: 'amount',
                  key: 'amount',
                  align: 'right' as const,
                  className: 'ins-num',
                  width: 100,
                  render: (v: number) => usdExact(v),
                },
              ]}
              tableData={data?.awardsByBusinessUnit || []}
            >
              <MagnitudeBars
                data={(data?.awardsByBusinessUnit || [])
                  .map((r) => ({
                    name: truncate(r.businessUnit, 22),
                    fullName: r.businessUnit,
                    value: r.awards,
                    awards: r.awards,
                    amount: r.amount,
                    recipients: r.recipients,
                  }))}
                valueFormat={(v) => num(v)}
                axisFormat={(v) => compactNum(v)}
                tooltipRows={[
                  { key: 'awards', name: 'awards', color: VIZ.series1, format: (v) => num(v) },
                  { key: 'amount', name: 'value', color: VIZ.series2, format: (v) => usdExact(v) },
                ]}
                labelWidth={150}
              />
            </ChartCard>
          </Col>

          <Col xs={24} lg={12}>
            <ChartCard
              title="Top departments"
              subtitle="Recipient department, from the HCM employee master"
              hasData={(data?.topDepartments?.length ?? 0) > 0}
              tableColumns={[
                {
                  title: 'Department',
                  dataIndex: 'department',
                  key: 'department',
                  render: (v: string) => departmentLabel(v),
                },
                {
                  title: 'Awards',
                  dataIndex: 'awards',
                  key: 'awards',
                  align: 'right' as const,
                  className: 'ins-num',
                  width: 100,
                  sorter: (a: any, b: any) => a.awards - b.awards,
                  render: (v: number) => num(v),
                },
                {
                  title: 'Value',
                  dataIndex: 'amount',
                  key: 'amount',
                  align: 'right' as const,
                  className: 'ins-num',
                  width: 100,
                  render: (v: number) => usdExact(v),
                },
                {
                  title: 'Recipients',
                  dataIndex: 'recipients',
                  key: 'recipients',
                  align: 'right' as const,
                  className: 'ins-num',
                  width: 100,
                  render: (v: number) => num(v),
                },
              ]}
              tableData={data?.topDepartments || []}
            >
              <MagnitudeBars
                data={(data?.topDepartments || [])
                  .map((r) => ({
                    name: truncate(departmentLabel(r.department), 24),
                    fullName: departmentLabel(r.department),
                    value: r.awards,
                    awards: r.awards,
                    amount: r.amount,
                    recipients: r.recipients,
                  }))}
                valueFormat={(v) => num(v)}
                axisFormat={(v) => compactNum(v)}
                tooltipRows={[
                  { key: 'awards', name: 'awards', color: VIZ.series1, format: (v) => num(v) },
                  { key: 'amount', name: 'value', color: VIZ.series2, format: (v) => usdExact(v) },
                  { key: 'recipients', name: 'recipients', color: VIZ.series3, format: (v) => num(v) },
                ]}
                labelWidth={170}
              />
            </ChartCard>
          </Col>

          <Col xs={24} lg={12}>
            <ChartCard
              title="Awards by core value"
              subtitle="Which company value gets recognised most"
              hasData={(data?.awardsByCoreValue?.length ?? 0) > 0}
              tableColumns={[
                { title: 'Core value', dataIndex: 'coreValue', key: 'coreValue' },
                {
                  title: 'Awards',
                  dataIndex: 'awards',
                  key: 'awards',
                  align: 'right' as const,
                  className: 'ins-num',
                  width: 100,
                  render: (v: number) => num(v),
                },
                {
                  title: 'Value',
                  dataIndex: 'amount',
                  key: 'amount',
                  align: 'right' as const,
                  className: 'ins-num',
                  width: 100,
                  render: (v: number) => usdExact(v),
                },
              ]}
              tableData={data?.awardsByCoreValue || []}
            >
              <MagnitudeBars
                data={(data?.awardsByCoreValue || [])
                  .map((r) => ({
                    name: truncate(r.coreValue, 22),
                    fullName: r.coreValue,
                    value: r.awards,
                    awards: r.awards,
                    amount: r.amount,
                  }))}
                valueFormat={(v) => num(v)}
                axisFormat={(v) => compactNum(v)}
                tooltipRows={[
                  { key: 'awards', name: 'awards', color: VIZ.series1, format: (v) => num(v) },
                  { key: 'amount', name: 'value', color: VIZ.series2, format: (v) => usdExact(v) },
                ]}
                labelWidth={150}
              />
            </ChartCard>
          </Col>

          <Col xs={24} lg={12}>
            <ChartCard
              title="Approval status"
              subtitle={`Where the ${num(summary?.totalAwards)} awards in this slice stand`}
              hasData={statusChart.length > 0}
              tableColumns={[
                { title: 'Status', dataIndex: 'status', key: 'status' },
                {
                  title: 'Awards',
                  dataIndex: 'awards',
                  key: 'awards',
                  align: 'right' as const,
                  className: 'ins-num',
                  width: 100,
                  render: (v: number) => num(v),
                },
                {
                  title: 'Share',
                  key: 'share',
                  align: 'right' as const,
                  className: 'ins-num',
                  width: 100,
                  render: (_: any, r: any) =>
                    `${statusTotal ? ((r.awards / statusTotal) * 100).toFixed(1) : '0.0'}%`,
                },
                {
                  title: 'Value',
                  dataIndex: 'amount',
                  key: 'amount',
                  align: 'right' as const,
                  className: 'ins-num',
                  width: 100,
                  render: (v: number) => usdExact(v),
                },
              ]}
              tableData={[...(data?.approvalStatus || [])].sort((a, b) => b.awards - a.awards)}
            >
              <div className="ins-donut-wrap">
                <ResponsiveContainer width="100%" height={212}>
                  <PieChart>
                    <Pie
                      data={statusChart}
                      dataKey="awards"
                      nameKey="status"
                      innerRadius={62}
                      outerRadius={92}
                      paddingAngle={2}
                      stroke={VIZ.surface}
                      strokeWidth={2}
                      isAnimationActive={false}
                    >
                      {statusChart.map((s) => (
                        <Cell key={s.status} fill={STATUS_STYLE[s.status]?.color || VIZ.series1} />
                      ))}
                    </Pie>
                    <Tooltip
                      content={({ active, payload }: any) => {
                        if (!active || !payload?.length) return null;
                        const p = payload[0].payload;
                        return (
                          <div className="ins-tip">
                            <div className="ins-tip-cat">{p.status}</div>
                            <div className="ins-tip-row">
                              <span
                                className="ins-tip-key"
                                style={{ background: STATUS_STYLE[p.status]?.color }}
                              />
                              <span className="ins-tip-value">{num(p.awards)}</span>
                              <span className="ins-tip-name">awards</span>
                            </div>
                            <div className="ins-tip-row">
                              <span className="ins-tip-key" style={{ background: VIZ.muted }} />
                              <span className="ins-tip-value">{usdExact(p.amount)}</span>
                              <span className="ins-tip-name">value</span>
                            </div>
                          </div>
                        );
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="ins-donut-center">
                  <div className="ins-donut-center-value">{num(statusTotal)}</div>
                  <div className="ins-donut-center-label">Awards</div>
                </div>
              </div>

              {/* Legend doubles as the readable value list, so nothing is colour-only. */}
              <div className="ins-legend" style={{ marginTop: 12, padding: '0 4px' }}>
                {statusChart.map((s) => (
                  <div className="ins-legend-row" key={s.status}>
                    <span
                      className="ins-swatch"
                      style={{ background: STATUS_STYLE[s.status]?.color || VIZ.series1 }}
                    />
                    <span>
                      <span style={{ color: STATUS_STYLE[s.status]?.color, marginRight: 5 }}>
                        {STATUS_STYLE[s.status]?.icon}
                      </span>
                      {s.status}
                      <AntTooltip title={STATUS_STYLE[s.status]?.hint}>
                        <InfoCircleOutlined style={{ color: VIZ.muted, fontSize: 10, marginLeft: 5 }} />
                      </AntTooltip>
                    </span>
                    <span className="ins-legend-value">{num(s.awards)}</span>
                    <span className="ins-legend-pct">
                      {statusTotal ? ((s.awards / statusTotal) * 100).toFixed(1) : '0.0'}%
                    </span>
                  </div>
                ))}
              </div>
            </ChartCard>
          </Col>

          <Col xs={24} lg={12}>
            <div className="ins-card">
              <div className="ins-card-head">
                <div>
                  <div className="ins-card-title">Top recipients</div>
                  <div className="ins-card-sub">
                    <RiseOutlined /> Highest award value received in this slice
                  </div>
                </div>
              </div>
              <div className="ins-card-body ins-table-scroll">
                <Table
                  size="small"
                  rowKey={(_r, i) => String(i)}
                  pagination={false}
                  scroll={{ y: 280 }}
                  dataSource={data?.topRecipients || []}
                  locale={{ emptyText: 'No recipients in this slice' }}
                  columns={[
                    { title: 'Recipient', dataIndex: 'nomineeName', key: 'nomineeName' },
                    { title: 'Country', dataIndex: 'country', key: 'country' },
                    {
                      title: 'Awards',
                      dataIndex: 'awards',
                      key: 'awards',
                      align: 'right' as const,
                      className: 'ins-num',
                  width: 100,
                      render: (v: number) => num(v),
                    },
                    {
                      title: 'Value',
                      dataIndex: 'amount',
                      key: 'amount',
                      align: 'right' as const,
                      className: 'ins-num',
                  width: 100,
                      sorter: (a: any, b: any) => a.amount - b.amount,
                      render: (v: number) => usdExact(v),
                    },
                  ]}
                />
              </div>
            </div>
          </Col>

          <Col xs={24} lg={12}>
            {/* Award volume and award spend are different units, so they get their own
                plots rather than a second y-axis on one chart. */}
            <Row gutter={[12, 12]}>
              <Col span={24}>
                <ChartCard
                  title={`Awards raised per ${granularityLabel}`}
                  subtitle="Volume of awards in the filtered slice"
                  hasData={(data?.awardTrend?.length ?? 0) > 0}
                >
                  <ResponsiveContainer width="100%" height={148}>
                    <BarChart data={data?.awardTrend || []} margin={{ top: 10, right: 12, left: 0, bottom: 4 }}>
                      <CartesianGrid vertical={false} stroke={VIZ.grid} strokeWidth={1} />
                      <XAxis
                        dataKey="label"
                        tick={{ fill: VIZ.muted, fontSize: 10 }}
                        axisLine={{ stroke: VIZ.axis }}
                        tickLine={false}
                        minTickGap={12}
                      />
                      <YAxis
                        tick={{ fill: VIZ.muted, fontSize: 10 }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(v) => compactNum(Number(v))}
                        width={38}
                      />
                      <Tooltip
                        cursor={{ fill: 'rgba(42,120,214,0.06)' }}
                        content={
                          <VizTooltip
                            rows={[{ key: 'awards', name: 'awards', color: VIZ.series1, format: (v: any) => num(v) }]}
                          />
                        }
                      />
                      <Bar
                        dataKey="awards"
                        fill={VIZ.series1}
                        radius={[4, 4, 0, 0]}
                        maxBarSize={22}
                        isAnimationActive={false}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Col>
              <Col span={24}>
                <ChartCard
                  title={`Award spend per ${granularityLabel}`}
                  subtitle="Value of awards in the filtered slice"
                  hasData={(data?.awardTrend?.length ?? 0) > 0}
                  tableColumns={[
                    { title: 'Period', dataIndex: 'label', key: 'label' },
                    {
                      title: 'Awards',
                      dataIndex: 'awards',
                      key: 'awards',
                      align: 'right' as const,
                      className: 'ins-num',
                  width: 100,
                      render: (v: number) => num(v),
                    },
                    {
                      title: 'Value',
                      dataIndex: 'amount',
                      key: 'amount',
                      align: 'right' as const,
                      className: 'ins-num',
                  width: 100,
                      render: (v: number) => usdExact(v),
                    },
                  ]}
                  tableData={data?.awardTrend || []}
                >
                  <ResponsiveContainer width="100%" height={148}>
                    <AreaChart data={data?.awardTrend || []} margin={{ top: 10, right: 12, left: 0, bottom: 4 }}>
                      <defs>
                        <linearGradient id="insSpendWash" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={VIZ.series1} stopOpacity={0.16} />
                          <stop offset="100%" stopColor={VIZ.series1} stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid vertical={false} stroke={VIZ.grid} strokeWidth={1} />
                      <XAxis
                        dataKey="label"
                        tick={{ fill: VIZ.muted, fontSize: 10 }}
                        axisLine={{ stroke: VIZ.axis }}
                        tickLine={false}
                        minTickGap={12}
                      />
                      <YAxis
                        tick={{ fill: VIZ.muted, fontSize: 10 }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(v) => compactUsd(Number(v))}
                        width={52}
                      />
                      <Tooltip
                        cursor={{ stroke: VIZ.axis, strokeWidth: 1 }}
                        content={
                          <VizTooltip
                            rows={[
                              { key: 'amount', name: 'award value', color: VIZ.series1, format: (v: any) => usdExact(v) },
                              { key: 'awards', name: 'awards', color: VIZ.series2, format: (v: any) => num(v) },
                            ]}
                          />
                        }
                      />
                      <Area
                        type="monotone"
                        dataKey="amount"
                        stroke={VIZ.series1}
                        strokeWidth={2}
                        fill="url(#insSpendWash)"
                        dot={false}
                        activeDot={{ r: 4, fill: VIZ.series1, stroke: VIZ.surface, strokeWidth: 2 }}
                        isAnimationActive={false}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Col>
            </Row>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col span={24}>
            <AwardsDrilldown rows={data?.awardDetails || []} />
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default AwardsInsights;
